import type { Locale } from "date-fns";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";

/**
 * Passenger-facing date patterns, per page language. Spanish puts the day
 * before the month ("mié, 30 dic 2026"); English keeps "Wed, Dec 30, 2026".
 * Without the date-fns locale, month and weekday names stay English on a
 * Spanish page (Pegasos 10-27).
 */
export type DatePattern =
  | "weekdayDateYear"
  | "weekdayDateYearDot"
  | "weekdayDate"
  | "weekdayLongDate";

const PATTERNS: Record<DatePattern, { en: string; es: string }> = {
  weekdayDateYear: { en: "EEE, MMM d, yyyy", es: "EEE, d MMM yyyy" },
  weekdayDateYearDot: { en: "EEE, MMM d · yyyy", es: "EEE, d MMM · yyyy" },
  weekdayDate: { en: "EEE, MMM d", es: "EEE, d MMM" },
  weekdayLongDate: { en: "EEEE, MMMM d", es: "EEEE, d 'de' MMMM" },
};

export interface DateLocale {
  /** date-fns locale; undefined = date-fns' English default. */
  locale: Locale | undefined;
  pattern: (name: DatePattern) => string;
}

export function dateLocaleFor(language: string | undefined): DateLocale {
  const isEs = language?.startsWith("es") ?? false;
  return {
    locale: isEs ? es : undefined,
    pattern: (name) => (isEs ? PATTERNS[name].es : PATTERNS[name].en),
  };
}

/** The current page language's date locale; re-renders on language change. */
export function useDateLocale(): DateLocale {
  const { i18n } = useTranslation();
  return dateLocaleFor(i18n.language);
}
